// /invite/{code} — the landing screen for an invite link.
//
// Per W2 §4.3 Sub-flow B: pre-fetches GET /api/v1/pair-invites/{code}/public
// (no auth).  Privacy: inviter's display_name is exposed ONLY when
// status === "pending".
//
// W54.0 A2 — the CTA branches on the session, because before this it was one
// link for everybody: `/register?invited_by_code=<code>`. For a person who is
// **already signed in** that was a dead end — `/register` is behind
// `RedirectIfAuthed`, which honours `?next=` and nothing else, so the invite
// code was dropped on the floor and the person landed on `/dashboard`. The
// only path to a pair through an invite link was 「not signed in AND not
// paired」, and 「already registered」 is the common case.
//
// The backend was always ready: `POST /api/v1/pairs/accept` exists and
// `acceptInvite()` was already written. Only the entrance was missing.
//
// The surgical fix is here rather than in `RedirectIfAuthed` (which `/login`
// and `/register` share — changing it would reach the whole sign-in flow) and
// rather than in `PairChoicePage` (which would leave two ways to redeem a
// code side by side). See `docs/spec/w54.0-verify-and-secure.md` §2 A2.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/primitives/EmptyState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { AuthShell } from "@/components/auth/AuthShell";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  acceptInvite,
  ApiError,
  readPublicInvite,
  type PublicInviteResponse,
} from "@/lib/api/pair";
import { PAIR_I18N, pairErrorMessage } from "@/lib/i18n/pair";
import { API_REQUEST_FAILED } from "@/lib/i18n/errors";
import { queryKeys } from "@/lib/queries/keys";

export function InviteLandingPage() {
  const params = useParams<{ code: string }>();
  const nav = useNavigate();
  const code = params.code ?? "";
  const { status: authStatus, pairId, refresh } = useAuth();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const inviteQuery = useQuery<PublicInviteResponse>({
    queryKey: queryKeys.pairs.publicInvite(code),
    queryFn: () => readPublicInvite(code),
    enabled: code.length === 6,
    retry: false,
  });

  const acceptMut = useMutation({
    mutationFn: () => acceptInvite({ code }),
    onSuccess: async () => {
      // The session's `pairId` is what the rest of the app branches on, and it
      // is still the old value until the session is re-read. Refreshing before
      // the navigation is what keeps `/pair` from bouncing the person back
      // here as "not paired".
      try {
        await refresh();
      } catch {
        // The pair exists — that is what the server just said. A session
        // refresh that does not come back must not strand the person on a
        // page that has nothing left to offer; the pair screen re-reads the
        // pair anyway.
      }
      nav("/settings/pair", { replace: true });
    },
    onError: (err) => {
      setAcceptError(
        err instanceof ApiError ? pairErrorMessage(err.code, err.message) : API_REQUEST_FAILED,
      );
    },
  });

  const body = (() => {
    if (!code || code.length !== 6) {
      return (
        <AuthShell title="邀請連結" backHref="/">
          <ErrorState title="邀請碼格式不正確" />
        </AuthShell>
      );
    }
    if (inviteQuery.isLoading) {
      return (
        <AuthShell title="讀取邀請中…" backHref="/">
          <div className="flex items-center gap-2 text-body text-ink-2">
            <Loader2 className="w-4 h-4 animate-spin" /> 讀取中…
          </div>
        </AuthShell>
      );
    }
    if (inviteQuery.isError) {
      return (
        <AuthShell title="邀請連結" backHref="/">
          <ErrorState
            title={PAIR_I18N.accept_unknown}
            description="可能已被取消或過期。"
            retryLabel="重新整理頁面"
            onRetry={() => inviteQuery.refetch()}
          />
        </AuthShell>
      );
    }
    const data = inviteQuery.data;
    if (!data) return null;

    switch (data.status) {
      case "pending":
        return (
          <AuthShell
            title={
              data.inviter_display_name
                ? `加入 ${data.inviter_display_name} 的空間`
                : "加入這個空間"
            }
            subtitle="透過對方的邀請連結, 跟對方一起建立 Pair。"
            backHref="/"
            footer={
              authStatus === "anonymous" ? (
                <Link to="/login" className="text-you-strong font-medium">
                  已經有帳號? 登入
                </Link>
              ) : null
            }
          >
            <Card>
              <div className="p-6 flex flex-col gap-4" data-testid="invite-pending">
                <p className="text-body text-ink-2">
                  對方正在等你加入 {data.inviter_display_name ?? "他們"} 的 heshe 空間。
                </p>
                <p className="text-body-sm text-ink-3">
                  過期時間:{" "}
                  {data.expires_at ? new Date(data.expires_at).toLocaleString("zh-Hant") : "未提供"}
                </p>

                {/* W54.0 A2 — the three states a person can be in when they open
                    an invite link, and the one CTA each of them gets.

                    * signed in, already paired → the pair exists; the invite is
                      not redeemable and must not be attempted (the backend
                      answers `ALREADY_IN_PAIR`, and the screen would be telling
                      the person to do something that cannot be done).
                    * signed in, not paired → redeem the code here, which is the
                      path that was missing.
                    * not signed in → register with the code, unchanged.

                    While the session probe is still running the answer is not
                    known yet, so the CTA waits rather than guessing: sending a
                    signed-in person to `/register` was the dead end this branch
                    exists to close. */}
                {authStatus === "authenticated" && pairId ? (
                  <div className="flex flex-col gap-2" data-testid="invite-already-paired">
                    <p className="text-body text-ink-2">你已經有配對了。</p>
                    <Link
                      to="/settings/pair"
                      className="text-you-strong font-medium"
                      data-testid="invite-existing-pair-link"
                    >
                      前往你們的空間設定
                    </Link>
                  </div>
                ) : (
                  <div className="flex gap-3 justify-end">
                    {authStatus === "anonymous" ? (
                      <>
                        <Button variant="ghost" onClick={() => nav("/login")} data-testid="invite-signin">
                          登入
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => nav(`/register?invited_by_code=${encodeURIComponent(code)}`)}
                          data-testid="invite-accept-cta"
                          leadingIcon={<ArrowRight className="w-3.5 h-3.5" />}
                        >
                          建立帳號並加入
                        </Button>
                      </>
                    ) : authStatus === "authenticated" ? (
                      <Button
                        variant="primary"
                        onClick={() => acceptMut.mutate()}
                        disabled={acceptMut.isPending}
                        data-testid="invite-accept-cta"
                        leadingIcon={<ArrowRight className="w-3.5 h-3.5" />}
                      >
                        {acceptMut.isPending ? "加入中…" : "加入這個空間"}
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        disabled
                        data-testid="invite-accept-cta"
                        leadingIcon={<Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      >
                        讀取中…
                      </Button>
                    )}
                  </div>
                )}

                {acceptError ? (
                  <p
                    role="alert"
                    className="text-body-sm text-partner-strong"
                    data-testid="invite-accept-error"
                  >
                    {acceptError}
                  </p>
                ) : null}
              </div>
            </Card>
          </AuthShell>
        );
      case "accepted":
        return (
          <AuthShell title="這個邀請已被接受" backHref="/">
            <EmptyState title={PAIR_I18N.invite_status_accepted} />
          </AuthShell>
        );
      case "cancelled":
        return (
          <AuthShell title="邀請已被取消" backHref="/">
            <EmptyState title={PAIR_I18N.invite_status_cancelled} />
          </AuthShell>
        );
      case "expired":
        return (
          <AuthShell title="邀請已過期" backHref="/">
            <EmptyState title={PAIR_I18N.invite_status_expired} />
          </AuthShell>
        );
      default:
        return null;
    }
  })();

  return body;
}
